import React, { useEffect, useRef, useCallback } from "react";
import { FocusSDK, getClientType, isFocusEnv } from "@/utils";
import {
  getPlatform,
  getStatus,
  handleMeetingSendMessage,
  joinMeeting,
  sendToWebView,
  getUnitName,
  handleLiveSendMessage,
  sendCardMessage,
  showHomeCreate,
  sendToWebViewEvent,
  setCurrentMeetingInfo
} from "./utils";
import ChatState from "@/types/chat/State";
import { message as toast } from "antd";
import logger from "@/utils/logger";
import { AVSDK, LiveInfoType, BasicUserInfo } from "./common";
import { getConferenceDeeplink, getJRTCToken } from "@/api/meeting";
import { MeetingPlatform } from "@/types/common";
import bus from "@/utils/bus";
import GlobalContext from "@/context/GlobalContext";
import { connect } from "dva";
import dayjs, { Dayjs } from "dayjs";
import { ScheduleDetailFrom } from "@/components/Calendars/Schedule/Detail";
import { NetState } from "@/models/netStatus";
import { openUserSelectionModal } from "@/utils/modals";
import { gatewayInstance } from "@/server/gateway";
import { useHistory } from "react-router-dom";
import { UserCardType } from "@/types/UserCard";
import PostMessageService from "@/utils/postMessageClient";
import ImService from "@/server/ImService";
import { getRecentChatSession } from "@/api/chat";
import SessionType from "@jd/jdee.im.sdk/lib/es/enum/SessionType";
import { generateMessageId } from "@jd/jdee.im.sdk/lib/es/utils/utils";

// 注册监听事件，只在打开应用注册一次
function InitControl({
  newPublicResult,
  openCreateScheduleModal,
  changeScheduleInfoDraw,
  allSessionList
}: any) {
  const { imService, currentEmployee, appRuntimeEnv, authInfo, localConfigData } = React.useContext(
    GlobalContext
  );
  const history = useHistory();
  const inited = useRef(false);
  const loading = useRef(false);
  const handleMeetingUI = useCallback(
    function (event: string) {
      if (!isFocusEnv()) {
        return;
      }
      if (loading.current) {
        return;
      }
      loading.current = true;
      getJRTCToken({
        currentEmployee
      })
        .then(([jrcToken, err]) => {
          if (err) {
            toast.error(err);
            return;
          }

          const { ddAppId, userId, selectedTeamId, teamUserInfo } = authInfo;

          const userInfo = {
            token: jrcToken.token,
            deviceType: jrcToken.deviceType,
            jrtcUserId: jrcToken.jrtcUserId,
            appId: jrcToken.appId,
            app: ddAppId,
            pin: userId,
            teamId: selectedTeamId || teamUserInfo.teamId,
            name: currentEmployee.name,
            avatar: teamUserInfo.headImg || "",
            deptName: currentEmployee.deptName
          };
          FocusSDK.sendIpcMessage(event, {
            userInfo,
            env: {
              domain: jrcToken.domain,
              rtcDomain: jrcToken.rtcDomain
            }
          });
          setTimeout(() => {
            sendToWebView("wv:meeting:showUI");
          }, 1500);
        })
        .finally(() => {
          setTimeout(() => {
            loading.current = false;
          }, 1000);
        });
    },
    [authInfo, currentEmployee]
  );

  const sendTextMsg = useCallback(
    async (body: any, toUsers: any) => {
      const imService = ImService.getInstance();
      const sendResult = await Promise.all(
        toUsers.map((user: any) => {
          return new Promise((resolve) => {
            let sessionId = user.pin;
            if (user.type !== SessionType.GROUP) {
              sessionId = imService.generateSingleSessionId(
                {
                  app: authInfo.ddAppId,
                  userId: authInfo.userId,
                  teamId: authInfo.teamUserInfo.teamId
                } as any,
                {
                  pin: user.pin,
                  app: user.app,
                  teamId: user.teamId
                }
              );
            }
            const newMessage = {
              sessionId,
              ...body,
              requestData: {
                sessionId
              },
              timestamp: new Date().getTime()
            } as any;
            const messageId = generateMessageId();
            imService
              .sendChatMessage(sessionId, messageId, newMessage, {
                skipCheckSession: true
              })
              .then((res: any) => {
                logger.debug("send chat message ok: ", res);
                resolve({
                  message: "消息发送成功",
                  status: 1
                });
              })
              .catch((e: any) => {
                logger.error("send chat message err", e);
                resolve({
                  message: "消息发送失败",
                  status: 0,
                  error: e
                });
              });
          });
        })
      );
      return sendResult;
    },
    [authInfo]
  );

  useEffect(() => {
    const handleRuntime = () => {
      sendToWebView("wv:runtime", { appRuntimeEnv, authInfo, currentEmployee });
    };

    const handleMeetingStatus = async (data: any) => {
      try {
        const status = await getStatus(data.avsdk);
        sendToWebView("wv:meeting:status", { status });
      } catch (error) {
        sendToWebView("wv:meeting:status", {
          message: error?.message || error
        });

        console.error(error);
      }
    };

    const handleWvJoinMeeting = ({
      avsdk,
      meetingNumber,
      meetingPassword,
      isaudiooff,
      isvideooff
    }: any) => {
      joinMeeting(avsdk, {
        meetingNumber,
        meetingPassword,
        isaudiooff,
        isvideooff
      })
        .then(() => {
          sendToWebView("wv:meeting:joinmeeting", {
            status: true
          });
        })
        .catch((err) => {
          console.error("err", err);
          sendToWebView("wv:meeting:joinmeeting", {
            status: false,
            message: err?.message || err
          });
        });
    };
    const handleSendMessage = (data: any) => {
      handleMeetingSendMessage(data);
      sendToWebView("wv:meeting:sendMessage", true);
    };

    const handleSchedule = (data: any = {}) => {
      const meetingPlatform = data?.avsdk ? getPlatform(data.avsdk) : getPlatform(AVSDK.JBRTC);
      if (data?.scheduleId) {
        changeScheduleInfoDraw({
          visible: true,
          isLive: !!data.isLive,
          scheduleId: data.scheduleId,
          selectedScheduleTime: dayjs(data.conferenceTimeStart),
          from: ScheduleDetailFrom.Meeting,
          openTime: dayjs()
        });
      } else {
        openCreateScheduleModal({
          visible: true,
          defaultAppointment: true,
          isLive: !!data.isLive,
          meetingPlatform
        });
      }
      sendToWebView("wv:meeting:schedule", true);
    };

    const handleNet = () => {
      sendToWebView("wv:meeting:net", {
        newPublicResult,
        isPrivateNet: FocusSDK.isPrivateNet()
      });
    };

    const handleUserSelect = (options: any) => {
      openUserSelectionModal(
        options,
        (data, close) => {
          try {
            const { result } = data.data;
            if (result.length) {
              sendToWebView(
                "wv:meeting:userSelect",
                result.map((r: any) => {
                  const tem = r.id.split(":");
                  return {
                    realName: r.name,
                    userId: tem[0],
                    teamId: tem[2],
                    appId: tem[1],
                    headImg: r.avatar as string
                  };
                })
              );
            }

            close();
          } catch (error) {
            console.error(error);
            if (close) {
              close();
            }
          }
        },
        authInfo as any
      );
    };

    const handlePreInvite = function (data: any) {
      if (isFocusEnv()) {
        FocusSDK.sendIpcMessage("jdmeet.addPreInviteUser", data);
        sendToWebView("wv:meeting:preInvite", true);
      }
    };

    const handleSendCardMessage = (data: {
      avsdk: AVSDK;
      meetingInfo: LiveInfoType;
      toUsers: BasicUserInfo[];
    }) => {
      sendCardMessage(data)
        .then((res) => {
          sendToWebView("wv:meeting:sendCardMessage", res);
        })
        .catch((e) => {
          console.error(e);
          sendToWebView("wv:meeting:sendCardMessage", false);
        });
    };
    FocusSDK.printLog("222 preLoadMeetingWin--->>>", localConfigData?.settings?.preLoadMeetingWin);
    if (
      !inited.current &&
      currentEmployee?.userId &&
      localConfigData?.settings?.preLoadMeetingWin
    ) {
      getJRTCToken({
        currentEmployee
      }).then(([jrcToken, err]) => {
        if (err) {
          return;
        }
        inited.current = true;
        FocusSDK.sendIpcMessage("jdmeet.init", {
          domain: jrcToken.domain,
          pin: currentEmployee.userId,
          teamId: currentEmployee.teamId,
          app: currentEmployee.app,
          rtcDomain: jrcToken.rtcDomain
        });
      });
    }

    const handleSendCommonMessage = async ({
      body,
      toUsers
    }: {
      body: any;
      toUsers: BasicUserInfo[];
    }) => {
      const sendResult = await sendTextMsg(body, toUsers);
      sendToWebView("wv:meeting:sendCommonMessage", sendResult);
    };

    const onGetRecentChatSession = () => {
      getRecentChatSession()
        .then((res) => {
          sendToWebView("wv:meeting:getRecentChatSession", res);
        })
        .catch((e) => {
          console.error(e);
          sendToWebView("wv:meeting:getRecentChatSession", false);
        });
    };

    const handleSendChatMsg = async ({ message, toUsers }: any) => {
      for (const user of toUsers) {
        if (user.type === SessionType.GROUP) {
          const sessionId = user.pin;
          const messageId = generateMessageId();
          await imService.sendChatMessage(sessionId, messageId, message);
        } else {
          const session = await imService.createSingleSession(user);
          if (session) {
            const messageId = generateMessageId();
            await imService.sendChatMessage(session.sessionId, messageId, message);
          }
        }
      }
      sendToWebView("wv:meeting:sendChatMessage", true);
    };

    bus.on("wv:runtime", handleRuntime);
    bus.on("wv:meeting:sendMessage", handleSendMessage);
    bus.on("wv:meeting:sendCommonMessage", handleSendCommonMessage);
    bus.on("wv:meeting:sendCardMessage", handleSendCardMessage);
    bus.on("wv:meeting:showUI", handleMeetingUI);
    bus.on("wv:meeting:preInvite", handlePreInvite);
    bus.on("wv:meeting:status", handleMeetingStatus);
    bus.on("wv:meeting:joinmeeting", handleWvJoinMeeting);
    bus.on("wv:meeting:schedule", handleSchedule);
    bus.on("wv:meeting:net", handleNet);
    bus.on("wv:meeting:userSelect", handleUserSelect);
    bus.on("wv:meeting:getRecentChatSession", onGetRecentChatSession);
    bus.on("wv:meeting:sendChatMessage", handleSendChatMsg);

    return () => {
      bus.off("wv:runtime", handleRuntime);
      bus.off("wv:meeting:sendMessage", handleSendMessage);
      bus.off("wv:meeting:sendCommonMessage", handleSendCommonMessage);
      bus.off("wv:meeting:sendCardMessage", handleSendCardMessage);
      bus.off("wv:meeting:showUI", handleMeetingUI);
      bus.off("wv:meeting:preInvite", handlePreInvite);
      bus.off("wv:meeting:status", handleMeetingStatus);
      bus.off("wv:meeting:joinmeeting", handleWvJoinMeeting);
      bus.off("wv:meeting:schedule", handleSchedule);
      bus.off("wv:meeting:net", handleNet);
      bus.off("wv:meeting:userSelect", handleUserSelect);
      bus.off("wv:meeting:getRecentChatSession", onGetRecentChatSession);
      bus.off("wv:meeting:sendChatMessage", handleSendChatMsg);
    };
  }, [
    appRuntimeEnv,
    authInfo,
    changeScheduleInfoDraw,
    currentEmployee,
    handleMeetingUI,
    imService,
    newPublicResult,
    openCreateScheduleModal,
    sendTextMsg
  ]);

  useEffect(() => {
    const hangleSendMessage = (data: any) => {
      const {
        avsdk,
        sessionState,
        meetingID,
        meetingTopic,
        meetingNumber,
        meetingPassword,
        toUsers
      } = data;

      handleMeetingSendMessage({
        avsdk,
        meetingInfo: {
          meetingID,
          meetingNumber,
          meetingTopic,
          password: meetingPassword
        },
        sessionState,
        toUsers
      });
    };

    const handleJoinSuccess = async (data: any, retry = false) => {
      logger.debug("会议信息: ", data);
      if (!data.roomId || !data.room) {
        return;
      }
      const [res, err] = await getConferenceDeeplink({
        meetingId: data.roomId,
        meetingCode: data.room,
        platform: MeetingPlatform.JBRTC
      });
      if (err || !res) {
        logger.error(err);
        FocusSDK.printLog(`get share link failed: ${err}`);
        if (retry) {
          // 重试一次获取链接
          handleJoinSuccess(data, true);
        }
        return;
      }
      if (isFocusEnv()) {
        FocusSDK.sendIpcMessage("jdmeet.setShareLink", res.shortUrl);
      } else {
        bus.emit("jdmeeting:setShareLink", res.shortUrl);
      }
    };

    const handleJoinMeetingCb = async ({
      status,
      data,
      error
    }: {
      status: number;
      data: any;
      error?: any;
    }) => {
      if (status) {
        handleJoinSuccess(data);
      } else {
        console.error(error || "加入会议失败");
      }
    };

    const handleReceiveMessage = (message: any) => {
      const avsdk = message.ext.avsdk;
      if (avsdk && avsdk === AVSDK.JBRTC) {
        // sdk二次呼叫
        if (isFocusEnv()) {
          FocusSDK.sendIpcMessage("jdmeet.setAttendeeStatus", {
            from: message.ext.fromUser,
            status: message.ext.sessionState
          });
        } else {
          PostMessageService.getInstance(AVSDK.JBRTC, false)?.postMessage("setAttendeeStatus", {
            from: message.ext.fromUser,
            status: message.ext.sessionState
          });
        }
      }
    };

    const handleInitCb = ({ status, error }: any) => {
      if (status === 0) {
        if (error === "DENIED") {
          toast.error("请授予麦克风和摄像头权限");
        } else {
          toast.error(error || "初始化失败");
        }
      }
    };

    const handleJoinMeeting = (opts: {
      avsdk: AVSDK;
      meetingInfo: {
        meetingNumber: string;
        meetingPassword: string;
        isvideooff: boolean;
        isaudiooff: boolean;
      };
    }) => {
      joinMeeting(opts.avsdk, opts.meetingInfo).catch((error) => {
        toast.error(error?.message || error || "加入会议失败");
      });
    };

    async function handleRequestGateway({ api, uid, body }: any) {
      try {
        const data: any = await gatewayInstance.request({
          api,
          method: "POST",
          body
        });
        FocusSDK.sendIpcMessage("common:requestGatewayCb", {
          uid,
          data: [data, null]
        });
      } catch (error) {
        FocusSDK.sendIpcMessage("common:requestGatewayCb", {
          uid,
          data: [null, error.message.toString()]
        });
      }
    }

    function handleLiveInvite(data: any) {
      handleLiveSendMessage({
        currentEmployee,
        avsdk: data.avsdk,
        meetingInfo: {
          meetingNumber: data.meetingNumber,
          password: data.password,
          liveCode: data.liveCode,
          meetingTopic: data.meetingTopic,
          livePassword: data.livePassword,
          initiator: currentEmployee.name,
          liveUrl: data.liveUrl,
          liveStartTime: data.liveStartTime
        },
        toUsers: data.toUsers
      });
    }

    function handleCardInvite(data: any) {
      sendCardMessage({
        avsdk: data.avsdk,
        toUsers: data.toUsers,
        meetingInfo: {
          meetingNumber: data.meetingNumber,
          password: data.password,
          liveCode: data.liveCode,
          meetingTopic: data.meetingTopic,
          livePassword: data.livePassword,
          liveUrl: data.liveUrl,
          liveStartTime: data.liveStartTime
        }
      });
    }

    function handleCreatShow(data: any) {
      showHomeCreate(AVSDK.ZOOM, data, history);
    }
    function handleAddContact(data: any) {
      FocusSDK.openUserCard(
        {
          type: UserCardType.CustomUser,
          ...data
        },
        () => {
          FocusSDK.sendIpcMessage("jdmeet:active");
        }
      );
    }

    async function hangleSendCommonMessage(data: any) {
      sendTextMsg(data.body, data.toUsers);
    }

    function handleLiveChatMessage(e: any) {
      sendToWebViewEvent("wv:liveChatMessage", AVSDK.JBRTC, e);
    }

    function handleLeaveMeeting(params: { avsdk: AVSDK }) {
      setCurrentMeetingInfo(params.avsdk, null);
    }

    FocusSDK.on("common:requestGateway", handleRequestGateway);
    FocusSDK.on("meeting:sendMessage", hangleSendMessage);
    FocusSDK.on("meeting:sendCommonMessage", hangleSendCommonMessage);
    FocusSDK.on("meeting:liveInvite", handleLiveInvite);
    FocusSDK.on("meeting:cardInvite", handleCardInvite);
    FocusSDK.on("meeting:joinMeeting", handleJoinMeeting);
    FocusSDK.on("jdmeet:joinMeetingCb", handleJoinMeetingCb);
    FocusSDK.on("jdmeet:initCb", handleInitCb);
    bus.on("jdmeeting:joinMeetingCb", handleJoinMeetingCb);
    bus.on("meeting:receive-chat-message", handleReceiveMessage);
    FocusSDK.on("meeting:create-show", handleCreatShow);
    FocusSDK.on("jdmeet:addContact", handleAddContact);
    FocusSDK.on("meeting:leave", handleLeaveMeeting);

    if (isFocusEnv()) {
      bus.on("live:live/ack-message", handleLiveChatMessage);
      bus.on("live:chat_new_message", handleLiveChatMessage);
      bus.on("chat:session-update", handleLiveChatMessage);
      // bus.on("calendar:reload", handleCalendarReload);
    }
    return () => {
      FocusSDK.off("common:requestGateway", handleRequestGateway);
      FocusSDK.off("meeting:sendMessage", hangleSendMessage);
      FocusSDK.off("meeting:sendCommonMessage", hangleSendCommonMessage);
      FocusSDK.off("meeting:liveInvite", handleLiveInvite);
      FocusSDK.off("meeting:cardInvite", handleCardInvite);
      FocusSDK.off("jdmeet:joinMeetingCb", handleJoinMeetingCb);
      FocusSDK.off("meeting:joinMeeting", handleJoinMeeting);
      FocusSDK.off("jdmeet:initCb", handleInitCb);
      bus.off("jdmeeting:joinMeetingCb", handleJoinMeetingCb);
      bus.off("meeting:receive-chat-message", handleReceiveMessage);
      FocusSDK.off("meeting:create-show", handleCreatShow);
      FocusSDK.off("jdmeet:addContact", handleAddContact);
      FocusSDK.off("meeting:leave", handleLeaveMeeting);

      if (isFocusEnv()) {
        bus.off("live:live/ack-message", handleLiveChatMessage);
        bus.off("live:chat_new_message", handleLiveChatMessage);
        bus.off("chat:session-update", handleLiveChatMessage);
        // bus.off("calendar:reload", handleCalendarReload);
      }
    };
  }, [allSessionList, authInfo, currentEmployee, history, sendTextMsg]);

  return null;
}

function mapStateToProps({ netStatus, chat }: { netStatus: NetState; chat: ChatState }) {
  return {
    newPublicResult: netStatus.newPublicResult,
    allSessionList: chat.allSessionList
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    openCreateScheduleModal(data: any) {
      dispatch({ type: "calendar/openCreateModal", payload: data });
    },
    changeScheduleInfoDraw: (payload: {
      visible: boolean;
      scheduleId: string;
      from: number;
      openTime: Dayjs;
    }) =>
      dispatch({
        type: "calendar/changeScheduleInfoDraw",
        payload
      })
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(InitControl);
